import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppLayout, AuthLayout } from './layouts';
import { useAuth } from './hooks';
import { WebSocketProvider } from './providers/WebSocketProvider';
import {
  Auth,
  AcceptInvite,
  BusinessCenter,
  Agents,
  AgentDetail,
  Applications,
  DataSources,
  Analytics,
  Profile,
  Payments,
} from './screens';

const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isInitializing } = useAuth();

  if (isInitializing) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
};

const RedirectIfAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isInitializing } = useAuth();
  const location = useLocation();

  if (isInitializing) {
    return null;
  }

  if (isAuthenticated) {
    const params = new URLSearchParams(location.search);
    const returnTo = params.get('returnTo');
    return <Navigate to={returnTo || '/agents'} replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => (
  <Routes>
    <Route path="/invite/:inviteCode" element={<AcceptInvite />} />

    <Route
      element={
        <RedirectIfAuth>
          <AuthLayout />
        </RedirectIfAuth>
      }
    >
      <Route path="/auth" element={<Auth />} />
    </Route>

    <Route
      element={
        <RequireAuth>
          <WebSocketProvider>
            <AppLayout />
          </WebSocketProvider>
        </RequireAuth>
      }
    >
      <Route path="/" element={<Navigate to="/agents" replace />} />
      <Route path="/agents" element={<Agents />} />
      <Route path="/agents/:id" element={<AgentDetail />} />
      <Route path="/applications" element={<Applications />} />
      <Route path="/partners" element={<Navigate to="/applications" replace />} />
      <Route path="/data-sources" element={<DataSources />} />
      <Route path="/analytics" element={<Analytics />} />
      <Route path="/business" element={<BusinessCenter />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/payments" element={<Payments />} />
    </Route>

    <Route path="*" element={<Navigate to="/agents" replace />} />
  </Routes>
);

export default App;
